/**
 * FirmCombobox.jsx — searchable firm picker.
 *
 * A lightweight take on the Implied design system's Combobox
 * (lumosity_clients web/common/components/ui/combobox.tsx), rebuilt for this
 * stack: type to filter, arrow keys + Enter to pick, Escape to close.
 *
 * Props:
 *   firms    — [{ firm_id, firm_name }] full list to search
 *   pinned   — [{ firm_id, firm_name, hint }] options shown on top before any
 *              typing (e.g. the domain-matched firm, "keep current firm")
 *   value    — selected firm_id ('' = none)
 *   onChange — (firm_id, firm) => void
 *   placeholder
 */

import { useMemo, useRef, useState } from 'react';

const MAX_RESULTS = 40;

export default function FirmCombobox({ firms = [], pinned = [], value = '', onChange, onCreate, placeholder = 'Search firms…' }) {
  const [query, setQuery]       = useState(null); // null = not editing; show selection
  const [open, setOpen]         = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  const byId = useMemo(() => {
    const m = new Map();
    for (const f of [...pinned, ...firms]) if (f.firm_id) m.set(f.firm_id, f);
    return m;
  }, [firms, pinned]);

  const selected = value ? byId.get(value) : null;

  const options = useMemo(() => {
    const q = (query ?? '').trim().toLowerCase();
    if (!q) {
      const pinnedIds = new Set(pinned.map((f) => f.firm_id));
      return [...pinned, ...firms.filter((f) => !pinnedIds.has(f.firm_id)).slice(0, MAX_RESULTS - pinned.length)];
    }
    const seen = new Set();
    const out = [];
    for (const f of [...pinned, ...firms]) {
      if (!f.firm_id || seen.has(f.firm_id)) continue;
      if (!String(f.firm_name ?? '').toLowerCase().includes(q)) continue;
      seen.add(f.firm_id);
      out.push(f);
      if (out.length >= MAX_RESULTS) break;
    }
    return out;
  }, [query, firms, pinned]);

  function pick(f) {
    onChange?.(f.firm_id, f);
    setQuery(null);
    setOpen(false);
  }

  function handleKeyDown(e) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) { setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter')  { e.preventDefault(); if (options[activeIdx]) pick(options[activeIdx]); }
    else if (e.key === 'Escape') { setOpen(false); setQuery(null); inputRef.current?.blur(); }
  }

  return (
    <div className="combobox">
      <input
        ref={inputRef}
        className="combobox__input"
        placeholder={placeholder}
        value={query ?? selected?.firm_name ?? ''}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIdx(0); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => { setOpen(false); setQuery(null); }, 150)}
        onKeyDown={handleKeyDown}
      />
      {(selected || query) && (
        <button
          type="button" className="combobox__clear" title="Clear"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { setQuery(''); setOpen(true); onChange?.('', null); inputRef.current?.focus(); }}
        >✕</button>
      )}
      {open && (
        <div className="combobox__list">
          {options.length === 0 && !onCreate && <div className="combobox__empty">No firms match “{query}”</div>}
          {onCreate && (query ?? '').trim()
            && !options.some((f) => String(f.firm_name ?? '').toLowerCase() === query.trim().toLowerCase()) && (
            <button
              type="button" className="combobox__item"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onCreate(query.trim()); setQuery(null); setOpen(false); }}
            >
              ＋ Create firm “{query.trim()}”
            </button>
          )}
          {options.map((f, i) => (
            <button
              type="button"
              key={f.firm_id}
              className="combobox__item"
              data-active={i === activeIdx}
              onMouseDown={(e) => e.preventDefault() /* keep input focus until pick */}
              onMouseEnter={() => setActiveIdx(i)}
              onClick={() => pick(f)}
            >
              {f.firm_name}
              {f.hint && <span className="combobox__item-hint">{f.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
